// src/dashboard/Dashboard.js

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useFeatures } from '../feature/FeatureContext';
import { useNavigate } from 'react-router-dom';
import { PageContentWrapper, PageHeader, PageDescription } from '../main/LayoutStyles';
import { DashboardContainer, FeatureCard, ArticlesSection } from './DashboardStyles';
import { ArticleCardStyled } from '../article/KnowledgeStyles';
import apiClient from '../services/api';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import {
    MessageSquare, Edit3, Zap, Lightbulb, ClipboardList, Users, Shield, LogIn, ArrowRight, Loader2, CalendarDays
} from 'lucide-react';

// === POPRAWIONA LOGIKA FILTROWANIA ===
const getDashboardNavItems = (features) => {
    const allItems = [
        { name: 'Czat AI', path: '/app/chat', icon: MessageSquare, requiresLogin: false, description: 'Porozmawiaj z AI, aby lepiej zrozumieć swoje myśli i emocje.', featureKey: 'AI_CHAT' },
        { name: 'Dziennik Refleksji', path: '/app/journal', icon: Edit3, requiresLogin: true, description: 'Zapisuj swoje przemyślenia, śledź nastrój i odkrywaj wzorce.', featureKey: 'JOURNAL' },
        { name: 'Ćwiczenia', path: '/app/exercises', icon: Zap, requiresLogin: false, description: 'Techniki relaksacyjne i mindfulness, które pomogą Ci zredukować stres.', featureKey: 'EXERCISES' },
        { name: 'Baza Wiedzy', path: '/app/knowledge', icon: Lightbulb, requiresLogin: false, description: 'Artykuły i poradniki na temat zdrowia psychicznego i rozwoju.', featureKey: 'KNOWLEDGE_BASE' },
        { name: 'Testy', path: '/app/tests', icon: ClipboardList, requiresLogin: false, description: 'Poznaj siebie lepiej dzięki testom o charakterze psychoedukacyjnym.', featureKey: 'TESTS' },
        { name: 'Specjaliści', path: '/app/psychologists', icon: Users, requiresLogin: false, description: 'Znajdź profesjonalne wsparcie i przeglądaj profile psychologów.', featureKey: 'PSYCHOLOGIST_LIST' },
        { name: 'Wizyty', path: '/app/appointments', icon: CalendarDays, requiresLogin: true, description: 'Umów lub zarządzaj swoimi wizytami u specjalistów.', featureKey: 'APPOINTMENTS' },
    ];

    // Filtrujemy na podstawie obiektu 'features', pokazując tylko te, które mają wartość `true`
    return allItems.filter(item => features[item.featureKey]);
};

const Dashboard = () => {
    const { currentUser } = useAuth();
    const { features, isLoading: isFeaturesLoading } = useFeatures();
    const navigate = useNavigate();

    const [articles, setArticles] = useState([]);
    const [loadingArticles, setLoadingArticles] = useState(true);

    const navItems = useMemo(() => getDashboardNavItems(features), [features]);

    useEffect(() => {
        if (features.KNOWLEDGE_BASE) {
            const fetchArticles = async () => {
                setLoadingArticles(true);
                try {
                    const response = await apiClient.get('/articles');
                    setArticles(response.data.slice(0, 5));
                } catch (error) {
                    console.error("Nie udało się pobrać artykułów na pulpit:", error);
                } finally {
                    setLoadingArticles(false);
                }
            };
            fetchArticles();
        } else {
            setLoadingArticles(false);
        }
    }, [features.KNOWLEDGE_BASE]);

    const handleNavigation = (path, requiresLogin) => {
        if (requiresLogin && !currentUser) {
            navigate('/login', { state: { from: { pathname: path }, message: `Aby uzyskać dostęp, musisz się zalogować.` } });
        } else {
            navigate(path);
        }
    };

    const handleArticleClick = (event, slug) => {
        if (event.target.closest('.swiper-button-next, .swiper-button-prev')) {
            return;
        }
        navigate(`/app/knowledge/${slug}`);
    };

    const renderArticleSlides = () => articles.map(article => (
        <SwiperSlide key={article.id}>
            <ArticleCardStyled onClick={(e) => handleArticleClick(e, article.slug)}>
                {article.imageUrl ? (
                    <img src={article.imageUrl} alt={article.title} className="article-card-image" />
                ) : (
                    <div className="article-image-placeholder">Brak obrazka</div>
                )}
                <div className="article-card-content">
                    {article.category && <p className="article-category">{article.category}</p>}
                    <h3>{article.title}</h3>
                    <p className="article-snippet">{article.snippet}</p>
                </div>
            </ArticleCardStyled>
        </SwiperSlide>
    ));

    if (isFeaturesLoading) {
        return <PageContentWrapper><div style={{textAlign: 'center'}}><Loader2 className="icon-spin"/></div></PageContentWrapper>
    }

    return (
        <PageContentWrapper className="custom-scrollbar">
            <PageHeader>Witaj, {currentUser?.username || 'Gościu'}!</PageHeader>
            <PageDescription>Wybierz moduł lub sprawdź nowości, od których chcesz zacząć swoją pracę nad dobrym samopoczuciem.</PageDescription>

            <DashboardContainer>
                {navItems.map(item => {
                    const isDisabled = item.requiresLogin && !currentUser;
                    return (
                        <FeatureCard
                            key={item.name}
                            onClick={() => !isDisabled && handleNavigation(item.path, item.requiresLogin)}
                            $disabled={isDisabled}
                            title={isDisabled ? `${item.name} (wymaga logowania)` : item.name}
                        >
                            <div className="card-icon-wrapper">
                                <item.icon />
                            </div>
                            <div className="card-content">
                                <h3>{item.name}</h3>
                                <p>{item.description}</p>
                            </div>
                            <div className="card-action-indicator">
                                {isDisabled ? <LogIn size={20} /> : <ArrowRight size={20} />}
                            </div>
                        </FeatureCard>
                    );
                })}
            </DashboardContainer>

            {features.KNOWLEDGE_BASE && !loadingArticles && articles.length > 0 && (
                <ArticlesSection>
                    <h2>Ostatnio w Bazie Wiedzy</h2>
                    <div className="mobile-slider">
                        <Swiper
                            modules={[Pagination, A11y, Mousewheel]}
                            direction="vertical"
                            slidesPerView={3}
                            spaceBetween={15}
                            mousewheel={true}
                            pagination={{ clickable: true, el: '.swiper-pagination-vertical' }}
                            className="swiper-vertical-custom"
                        >
                            {renderArticleSlides()}
                            <div className="swiper-pagination-vertical"></div>
                        </Swiper>
                    </div>

                    <div className="desktop-slider">
                        <Swiper
                            modules={[Navigation, Pagination, A11y]}
                            spaceBetween={15}
                            slidesPerView={1}
                            navigation
                            pagination={{ clickable: true }}
                            breakpoints={{
                                768: { slidesPerView: 2, spaceBetween: 15 },
                                1200: { slidesPerView: 3, spaceBetween: 15 },
                            }}
                        >
                            {renderArticleSlides()}
                        </Swiper>
                    </div>
                </ArticlesSection>
            )}
        </PageContentWrapper>
    );
};

export default Dashboard;